import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const MODALITIES = ['Run', 'Bike', 'Erg', 'Row', 'Strength', 'Recovery', 'Other'];

const MODALITY_ICONS = {
  Run: '🏃',
  Bike: '🚴',
  Erg: '⚙️',
  Row: '🚣',
  Strength: '💪',
  Recovery: '🧘',
  Other: '🏋️'
};

const MODALITY_COLORS = {
  Run: 'bg-green-100 text-green-800 border-green-200',
  Bike: 'bg-blue-100 text-blue-800 border-blue-200',
  Erg: 'bg-orange-100 text-orange-800 border-orange-200',
  Row: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  Strength: 'bg-red-100 text-red-800 border-red-200',
  Recovery: 'bg-pink-100 text-pink-800 border-pink-200',
  Other: 'bg-purple-100 text-purple-800 border-purple-200'
};

function formatMinutes(minutes) {
  if (!minutes) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatDistance(meters) {
  if (!meters) return null;
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)}km`;
  }
  return `${meters}m`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [month, day, year] = dateStr.split('/');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function TrainingReports() {
  const { token, isAdmin, user } = useAuth();
  const todayIso = new Date().toISOString().slice(0, 10);
  const startOfYearIso = `${new Date().getFullYear()}-01-01`;
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'logs'
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [selectedTeam, setSelectedTeam] = useState('');
  const [startDate, setStartDate] = useState(startOfYearIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [selectedModalities, setSelectedModalities] = useState([]); // Multi-select modalities
  const [leaderboardMetric, setLeaderboardMetric] = useState('minutes'); // 'minutes' | 'onWaterKm'
  const [squads, setSquads] = useState([]);
  const [selectedSquad, setSelectedSquad] = useState(''); // '' = all, 'none' = unassigned, or squad id

  // The team whose squads are in scope: coach's own, or admin's team filter.
  const scopedTeam = !isAdmin() ? (user?.team || '') : selectedTeam;

  useEffect(() => {
    if (isAdmin()) {
      fetchTeams();
    }
    fetchData();
  }, [selectedTeam, startDate, endDate, selectedModalities, activeTab, selectedSquad]);

  useEffect(() => {
    setSelectedSquad('');
    if (!scopedTeam) {
      setSquads([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/squads?team=${encodeURIComponent(scopedTeam)}`, {
          headers: getAuthHeaders(token)
        });
        if (res.ok) {
          const data = await res.json();
          setSquads(data.squads || []);
        } else {
          setSquads([]);
        }
      } catch {
        setSquads([]);
      }
    })();
  }, [scopedTeam, token]);

  const formatIsoDateForApi = (isoDate) => {
    if (!isoDate || !isoDate.includes('-')) return null;
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return null;
    return `${month}/${day}/${year}`;
  };

  const getDateRangeLabel = () => {
    if (!startDate && !endDate) return 'All Time';
    if (startDate && endDate) return `${startDate} to ${endDate}`;
    if (startDate) return `From ${startDate}`;
    return `Through ${endDate}`;
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    const parsedStartDate = formatIsoDateForApi(startDate);
    const parsedEndDate = formatIsoDateForApi(endDate);
    if (parsedStartDate) params.append('startDate', parsedStartDate);
    if (parsedEndDate) params.append('endDate', parsedEndDate);
    // Only append team if it's not empty (empty string would still be sent)
    if (selectedTeam && selectedTeam.trim() !== '') {
      params.append('team', selectedTeam);
    }
    // Support multiple modalities
    if (selectedModalities.length > 0) {
      params.append('modalities', selectedModalities.join(','));
    }
    if (selectedSquad) {
      params.append('squadId', selectedSquad);
    }
    return params.toString();
  };

  const toggleModality = (modality) => {
    setSelectedModalities(prev => 
      prev.includes(modality)
        ? prev.filter(m => m !== modality)
        : [...prev, modality]
    );
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/training-logs/teams`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'summary') {
        await fetchSummary();
      } else {
        await fetchLogs();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const queryParams = buildQueryParams();
      const url = `${API_URL}/api/training-logs/summary${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        console.error('Failed to fetch training summary');
      }
    } catch (err) {
      console.error('Error fetching training summary:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      const queryParams = buildQueryParams();
      const url = `${API_URL}/api/training-logs${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        console.error('Failed to fetch training logs');
      }
    } catch (err) {
      console.error('Error fetching training logs:', err);
    }
  };

  const getLeaderboardUsers = () => {
    const rows = summary?.byUser || [];
    if (leaderboardMetric === 'minutes') {
      return [...rows].sort(
        (a, b) => (parseInt(b.total_minutes, 10) || 0) - (parseInt(a.total_minutes, 10) || 0)
      );
    }
    return [...rows].sort(
      (a, b) => (parseInt(b.on_water_meters, 10) || 0) - (parseInt(a.on_water_meters, 10) || 0)
    );
  };

  const getLeaderboardMax = (users) => {
    if (!users.length) return 1;
    if (leaderboardMetric === 'minutes') {
      return Math.max(...users.map((u) => parseInt(u.total_minutes, 10) || 0), 1);
    }
    return Math.max(...users.map((u) => parseInt(u.on_water_meters, 10) || 0), 1);
  };

  // Export functions
  const exportToCSV = () => {
    if (activeTab === 'summary' && summary?.byUser) {
      const headers = ['Athlete', 'Team', 'Total Minutes', 'Total Distance (m)', 'Log Count'];
      const rows = summary.byUser.map(u => [
        u.user_name || '',
        u.team || '',
        u.total_minutes || 0,
        u.total_distance || 0,
        u.log_count || u.entry_count || 0
      ]);
      downloadCSV(headers, rows, 'training_summary_custom_range.csv');
    } else if (activeTab === 'logs' && logs.length > 0) {
      const headers = ['Date', 'Athlete', 'Team', 'Modality', 'Minutes', 'Distance (m)', 'Equipment', 'Notes'];
      const rows = logs.map(log => [
        log.log_date || '',
        log.user_name || log.athlete_name || '',
        log.team || log.user_team || log.athlete_team || '',
        log.modality || '',
        log.minutes || 0,
        log.distance_meters || 0,
        log.equipment_name
          ? `${log.equipment_name}${log.equipment_class ? ` (${log.equipment_class})` : ''}`
          : '',
        log.notes || ''
      ]);
      downloadCSV(headers, rows, 'training_logs.csv');
    }
  };

  const downloadCSV = (headers, rows, filename) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportToPDF = () => {
    const totalMinutes = summary?.overall?.total_minutes || 0;
    const totalDistance = summary?.overall?.total_distance || 0;
    const byUser = summary?.byUser || [];
    const byModality = summary?.byModality || [];

    let content = `
      <html>
      <head>
        <title>Training Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
          h2 { color: #666; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .summary-box { display: inline-block; padding: 15px; margin: 10px; background: #f9f9f9; border-radius: 8px; }
          .total { font-size: 24px; font-weight: bold; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Training Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <p>Period: ${getDateRangeLabel()}</p>
        ${selectedTeam ? `<p>Team: ${selectedTeam}</p>` : ''}
        
        <div class="summary-box">
          <div>Total Minutes</div>
          <div class="total">${formatMinutes(totalMinutes)}</div>
        </div>
        <div class="summary-box">
          <div>Total Distance</div>
          <div class="total">${formatDistance(totalDistance) || '0m'}</div>
        </div>
        <div class="summary-box">
          <div>Athletes</div>
          <div class="total">${byUser.length}</div>
        </div>

        <h2>By Modality</h2>
        <table>
          <tr><th>Modality</th><th>Total Minutes</th><th>Total Distance</th></tr>
          ${byModality.map(m => `<tr><td>${MODALITY_ICONS[m.modality] || ''} ${m.modality}</td><td>${formatMinutes(parseInt(m.total_minutes))}</td><td>${formatDistance(m.total_distance) || '-'}</td></tr>`).join('')}
        </table>

        <h2>By Athlete</h2>
        <table>
          <tr><th>Athlete</th><th>Team</th><th>Total Minutes</th><th>Total Distance</th><th>Logs</th></tr>
          ${byUser.map(u => `<tr><td>${u.user_name}</td><td>${u.team || '-'}</td><td>${formatMinutes(parseInt(u.total_minutes))}</td><td>${formatDistance(u.total_distance) || '-'}</td><td>${u.log_count}</td></tr>`).join('')}
        </table>

        ${activeTab === 'logs' ? `
          <h2>Individual Logs</h2>
          <table>
            <tr><th>Date</th><th>Athlete</th><th>Modality</th><th>Minutes</th><th>Distance</th><th>Equipment</th><th>Notes</th></tr>
            ${logs.slice(0, 100).map(log => `<tr><td>${formatDate(log.log_date)}</td><td>${log.user_name}</td><td>${log.modality}</td><td>${log.minutes}</td><td>${formatDistance(log.distance_meters) || '-'}</td><td>${log.equipment_name ? `${log.equipment_name}${log.equipment_class ? ` (${log.equipment_class})` : ''}` : '-'}</td><td>${log.notes || '-'}</td></tr>`).join('')}
          </table>
          ${logs.length > 100 ? '<p><em>Showing first 100 logs</em></p>' : ''}
        ` : ''}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="p-2 sm:p-6 pb-24 sm:pb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-theme-primary">
          Training Reports
          {!isAdmin() && user?.team && (
            <span className="text-base font-normal text-gray-500 ml-2">({user.team})</span>
          )}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            CSV
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-4 border-b -mx-2 sm:mx-0 px-2 sm:px-0 overflow-x-auto">
        <button
          onClick={() => setActiveTab('summary')}
          className={`pb-3 px-4 font-semibold transition-colors whitespace-nowrap min-h-[44px] ${
            activeTab === 'summary'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700 active:bg-gray-100'
          }`}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`pb-3 px-4 font-semibold transition-colors whitespace-nowrap min-h-[44px] ${
            activeTab === 'logs'
              ? 'border-b-2 border-theme-primary text-theme-primary'
              : 'text-gray-500 hover:text-gray-700 active:bg-gray-100'
          }`}
        >
          All Logs
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <div className="w-full sm:w-auto">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base w-full sm:w-44"
              />
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base w-full sm:w-44"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                className="border px-3 py-2 rounded bg-gray-50 hover:bg-gray-100 text-sm w-full sm:w-auto"
              >
                Clear Range
              </button>
            </div>
          </div>

          {isAdmin() && (
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base w-full sm:w-48"
            >
              <option value="">All Teams</option>
              {teams.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          )}

          {squads.length > 0 && (
            <select
              value={selectedSquad}
              onChange={(e) => setSelectedSquad(e.target.value)}
              className="border p-3 sm:p-2 rounded bg-white text-gray-800 text-base w-full sm:w-48"
            >
              <option value="">All Squads</option>
              {squads.map((squad) => (
                <option key={squad.id} value={squad.id}>{squad.name}</option>
              ))}
              <option value="none">Unassigned</option>
            </select>
          )}
        </div>

        {/* Activity Type Multi-Select Filter */}
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
            Filter by Activity {selectedModalities.length > 0 && `(${selectedModalities.length} selected)`}
          </label>
          <div className="flex flex-wrap gap-2">
            {MODALITIES.map((mod) => (
              <button
                key={mod}
                type="button"
                onClick={() => toggleModality(mod)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  selectedModalities.includes(mod)
                    ? `${MODALITY_COLORS[mod]} ring-2 ring-offset-1`
                    : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                }`}
              >
                <span className="mr-1">{MODALITY_ICONS[mod]}</span>
                {mod}
              </button>
            ))}
            {selectedModalities.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedModalities([])}
                className="px-3 py-1.5 rounded-full text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-gray-500">Loading...</div>
      ) : activeTab === 'summary' ? (
        /* Summary View */
        <div className="space-y-6">
          {/* Overall Stats */}
          {summary?.overall && (
            <div className="bg-theme-bg-secondary p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">Overview</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-theme-primary">
                    {formatMinutes(parseInt(summary.overall.total_minutes) || 0)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Training</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-theme-primary">
                    {summary.overall.total_entries || 0}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Log Entries</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-theme-primary">
                    {Number(summary.overall.total_distance || 0).toLocaleString()}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Meters</p>
                </div>
              </div>
            </div>
          )}

          {/* By Modality */}
          {summary?.byModality && summary.byModality.length > 0 && (
            <div className="bg-white border rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">By Activity</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {summary.byModality.map((item) => (
                  <div
                    key={item.modality}
                    className={`p-3 sm:p-4 rounded-lg border text-center ${MODALITY_COLORS[item.modality]}`}
                  >
                    <span className="text-2xl">{MODALITY_ICONS[item.modality]}</span>
                    <p className="font-bold text-lg mt-1">{formatMinutes(parseInt(item.total_minutes) || 0)}</p>
                    <p className="text-xs">{item.modality}</p>
                    <p className="text-xs opacity-75">{item.entry_count} entries</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Squad */}
          {summary?.bySquad && summary.bySquad.length > 1 && (
            <div className="bg-white border rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">By Squad</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Squad</th>
                      <th className="px-3 py-2 text-right">Athletes</th>
                      <th className="px-3 py-2 text-right">Logs</th>
                      <th className="px-3 py-2 text-right">Total Training</th>
                      <th className="px-3 py-2 text-right">Avg / Athlete</th>
                      <th className="px-3 py-2 text-right">On-water km</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.bySquad.map((squad) => (
                      <tr key={squad.squadId ?? 'none'} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-700">{squad.squadName}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{squad.athleteCount}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{squad.logCount}</td>
                        <td className="px-3 py-2 text-right font-semibold text-theme-primary">
                          {formatMinutes(squad.totalMinutes)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">
                          {squad.athleteCount > 0 ? formatMinutes(Math.round(squad.totalMinutes / squad.athleteCount)) : '-'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">
                          {((squad.onWaterMeters || 0) / 1000).toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* By User (Leaderboard) */}
          {summary?.byUser && summary.byUser.length > 0 && (
            <div className="bg-white border rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Training Leaderboard</h3>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden p-0.5 bg-gray-50 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => setLeaderboardMetric('minutes')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      leaderboardMetric === 'minutes'
                        ? 'bg-theme-primary text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-200/80'
                    }`}
                  >
                    Total minutes
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeaderboardMetric('onWaterKm')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      leaderboardMetric === 'onWaterKm'
                        ? 'bg-theme-primary text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-200/80'
                    }`}
                  >
                    On-water km
                  </button>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-2">
                {(() => {
                  const boardUsers = getLeaderboardUsers();
                  const maxVal = getLeaderboardMax(boardUsers);
                  return boardUsers.map((user, index) => {
                    const raw =
                      leaderboardMetric === 'minutes'
                        ? parseInt(user.total_minutes, 10) || 0
                        : parseInt(user.on_water_meters, 10) || 0;
                    const percentage = (raw / maxVal) * 100;
                    const label =
                      leaderboardMetric === 'minutes'
                        ? formatMinutes(raw)
                        : `${(raw / 1000).toFixed(1)} km`;
                    return (
                    <div key={user.user_id} className="flex items-center gap-3 p-2 sm:p-1 rounded-lg hover:bg-gray-50 -mx-2 sm:mx-0">
                      <span className={`w-8 h-8 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-sm sm:text-xs font-bold flex-shrink-0 ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-orange-300 text-orange-900' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-1 gap-2">
                          <span className="font-medium text-sm sm:text-base truncate">{user.user_name}</span>
                          <span className="text-sm font-semibold text-theme-primary whitespace-nowrap">
                            {label}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 sm:h-2">
                          <div
                            className={`rounded-full h-2.5 sm:h-2 transition-all duration-500 ${
                              leaderboardMetric === 'onWaterKm' ? 'bg-cyan-600' : 'bg-theme-primary'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            </div>
          )}

          {(!summary?.byUser || summary.byUser.length === 0) && (
            <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
              <p>No training data for the selected period</p>
            </div>
          )}
        </div>
      ) : (
        /* Logs View */
        <div className="space-y-2 sm:space-y-3">
          {logs.length === 0 ? (
            <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
              <p className="text-lg mb-2">No training logs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className="border rounded-lg p-4 bg-white shadow-sm active:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  {/* Modality Badge */}
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${MODALITY_COLORS[log.modality]} w-fit`}>
                    <span className="text-base">{MODALITY_ICONS[log.modality]}</span>
                    <span>{log.modality}</span>
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-theme-primary text-base">{log.user_name || log.athlete_name}</span>
                      {(log.team || log.user_team || log.athlete_team) && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                          {log.team || log.user_team || log.athlete_team}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-1.5">
                      <span className="font-semibold text-theme-primary">{formatMinutes(log.minutes)}</span>
                      {log.distance_meters && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="font-medium text-gray-700">{formatDistance(log.distance_meters)}</span>
                        </>
                      )}
                      <span className="text-gray-400">•</span>
                      <span>{formatDate(log.log_date)}</span>
                    </div>
                    {log.notes && (
                      <p className="text-sm text-gray-500 mt-2 italic line-clamp-2">{log.notes}</p>
                    )}
                    {(log.equipment_name || log.equipment_id) && (
                      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                        <p className="text-sm font-semibold text-theme-primary">
                          Equipment: {log.equipment_name || `ID ${log.equipment_id}`}
                          {log.equipment_class ? (
                            <span className="text-gray-600 font-normal"> ({log.equipment_class})</span>
                          ) : null}
                        </p>
                        {log.distance_meters ? (
                          <p className="text-sm text-gray-600">
                            {formatDistance(log.distance_meters)} added to this equipment&apos;s meter log.
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

